import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import style from './index.module.css';

const DROPDOWN_WIDTH = 208;

interface IAction {
  title: string;
  action: string | ((props: any) => void);
}

interface IActionDropwdownCell {
  id: number;
  actions: IAction[];
  activeTooltipId: number | null;
  onTooltipToggle: (id: number) => void;
  tooltipRef?: any;
}

export const ActionDropwdownCell: React.FC<IActionDropwdownCell> = ({
  id,
  actions,
  activeTooltipId,
  onTooltipToggle,
  tooltipRef,
}) => {
  const isTooltipActive = activeTooltipId === id;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!isTooltipActive) {
      setDropdownPosition(null);
      return;
    }
    const updatePosition = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPosition({
        left: rect.right - DROPDOWN_WIDTH,
        top: rect.top + rect.height + 4,
      });
    };
    updatePosition();

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideButton = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (insideButton || insideDropdown) return;
      onTooltipToggle(id);
    };
    const handleScrollOrResize = () => {
      onTooltipToggle(id);
    };

    document.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isTooltipActive, id, onTooltipToggle]);

  const dropdownContent = dropdownPosition && isTooltipActive && (
    <div
      ref={(el) => {
        (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (typeof tooltipRef === 'function') tooltipRef(el);
        else if (tooltipRef?.current !== undefined) (tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }}
      className={classNames(
        style.tooltip,
        'fixed z-[99999] flex flex-col min-w-[208px] p-0 rounded-lg drop-shadow-md'
      )}
      style={{
        left: dropdownPosition.left,
        top: dropdownPosition.top,
        transform: 'none',
      }}
    >
      {actions?.map((item, index) => (
        <div key={`action-${item?.title}-${index}`} className="flex">
          {typeof item?.action === 'string' ? (
            <Link
              to={item?.action}
              className={classNames(
                'flex w-full h-10 items-center text-sm justify-between'
              )}
            >
              <button
                className={classNames(
                  'flex w-full h-10 items-center px-4 duration-200 text-sm justify-between'
                )}
                onClick={() => onTooltipToggle(id)}
              >
                <span>{item?.title}</span>
              </button>
            </Link>
          ) : (
            <button
              onClick={() => {
                onTooltipToggle(id);
                if (typeof item?.action === 'function') item.action(undefined);
              }}
              className={classNames(
                'flex w-full h-10 items-center px-4 duration-200 text-sm justify-between'
              )}
            >
              <span>{item?.title}</span>
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div ref={wrapperRef} className="w-7 relative">
      <button
        type="button"
        onClick={() => onTooltipToggle(id)}
        className={classNames('h-6 text-sm items-center gap-1 flex')}
      >
        <img
          src="/icons/ellipsis-vertical.svg"
          className="block h-6 w-6 duration-200 group-hover:brightness-200 group-hover:invert-1"
        />
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default ActionDropwdownCell;
